import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';

export class MeasureTool {
  active = false;
  distance = '';

  private pendingStart: THREE.Vector3 | null = null;

  private readonly startMarker = new THREE.Mesh(
    new THREE.SphereGeometry(10, 10, 20),
    new THREE.MeshBasicMaterial({ color: 0x10b981 }),
  );
  private readonly endMarker = this.startMarker.clone();

  private readonly lineGeometry = (() => {
    const color = new THREE.Color(0x10b981);
    return new LineGeometry()
      .setFromPoints([new THREE.Vector3(), new THREE.Vector3()])
      .setColors([color.r, color.g, color.b, color.r, color.g, color.b]);
  })();
  private readonly lineMaterial = new LineMaterial({
    linewidth: 5,
    vertexColors: true,
  });
  private readonly line = new Line2(this.lineGeometry, this.lineMaterial);

  addToScene(scene: THREE.Scene): void {
    this.startMarker.visible = false;
    this.endMarker.visible = false;
    this.line.visible = false;
    scene.add(this.startMarker, this.endMarker, this.line);
  }

  updateResolution(width: number, height: number): void {
    this.lineMaterial.resolution.set(width, height);
  }

  toggle(): void {
    if (this.active) {
      this.deactivate();
    } else {
      this.active = true;
      this.reset();
    }
  }

  deactivate(): void {
    this.active = false;
    this.reset();
  }

  handleClick(
    event: MouseEvent,
    canvas: HTMLCanvasElement,
    camera: THREE.PerspectiveCamera,
    objectGroup: THREE.Group,
  ): void {
    event.preventDefault();

    const intersections = getMeshIntersections(
      event,
      canvas,
      camera,
      objectGroup,
    );

    if (intersections.length === 0) return;

    const hitPoint = intersections[0].point;

    if (!this.pendingStart) {
      this.pendingStart = hitPoint.clone();
      this.startMarker.position.copy(hitPoint);
      this.startMarker.visible = true;
      this.endMarker.visible = false;
      this.line.visible = false;
      this.distance = '';
      return;
    }

    this.lineGeometry.setFromPoints([this.pendingStart, hitPoint]);
    this.endMarker.position.copy(hitPoint);
    this.endMarker.visible = true;
    this.line.visible = true;
    this.distance = this.pendingStart.distanceTo(hitPoint).toFixed(2);
    this.pendingStart = null;
  }

  private reset(): void {
    this.pendingStart = null;
    this.distance = '';
    this.line.visible = false;
    this.startMarker.visible = false;
    this.endMarker.visible = false;
  }
}

function getMeshIntersections(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.PerspectiveCamera,
  objectGroup: THREE.Group,
): THREE.Intersection[] {
  const rect = canvas.getBoundingClientRect();
  const mouse = new THREE.Vector2();

  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);

  const intersections = raycaster.intersectObjects(objectGroup.children, true);

  return intersections.filter(
    (intersection) =>
      intersection.object instanceof THREE.Mesh && intersection.object.visible,
  );
}
